import type Uri from "jsuri";
import includes from "lodash/includes";

// interfaces definitions
interface IError {
	message: string;
	stack?: string;
	extra?: {
		url: string;
		params: object;
	};
}

export interface IAdditionalOptions {
	dataType?: string;
	externalUrl?: boolean;
	useCookieOnly?: boolean;
}

export const defaultAdditionalOptions: IAdditionalOptions = {
	dataType: "",
	externalUrl: false,
	useCookieOnly: false,
};

const baseParams: RequestInit = {
	// adding this because according to MDN, fetch will set credentials to same-origin by default,
	// which will let to inclusion of cookies into the request
	// https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API/Using_Fetch
	credentials: "omit",
	headers: {
		Accept: "application/json",
	},
	signal: null,
};

// helper functions
const errorHandler = (response: Response) => {
	return response.text().then((body) => {
		if (body == null || body === "") {
			const error = new Error(body);
			Object.assign(error, { status: response.status }, { statusText: response.statusText });
			throw error;
		}
		let parsedError;
		try {
			parsedError = JSON.parse(body);
		} catch (e) {
			// tslint:disable-next-line:no-console
			console.error(e);
		}
		if (parsedError) {
			const error = new Error(parsedError?.message || body);
			Object.assign(
				error,
				parsedError,
				{ isApiError: true },
				{ status: response.status },
				{ statusText: response.statusText },
			);
			throw error;
		} else {
			const error = new Error(body);
			Object.assign(error, { status: response.status }, { statusText: response.statusText });
			throw error;
		}
	});
};
const responseHandler = <T>(response: Response, dataType?: string): Promise<T | string> => {
	try {
		if (response.ok) {
			const contentType = response.headers.get("content-type");
			if (dataType !== "text" && contentType && includes(contentType, "json")) {
				return response
					.json()
					.then((j) => Promise.resolve(j))
					.catch(() => Promise.resolve({}));
			} else if (dataType === "text" || (contentType && includes(contentType, "text"))) {
				return response.text();
			} else {
				// Defaulting to text if content type cannot be determined
				// https://github.com/github/fetch/issues/268#issuecomment-176544728
				return response
					.text()
					.then((j) => Promise.resolve(j ? JSON.parse(j) : {}))
					.catch(() => Promise.resolve({}));
			}
		} else {
			return errorHandler(response);
		}
	} catch {
		return errorHandler(response);
	}
};

const isError = (error: IError): boolean => {
	return error && error.message != null;
};

const processCaughtError = (uri: string, params: RequestInit, error: IError) => {
	if (isError(error)) {
		error.extra = {
			url: uri.toString(),
			params,
		};
	}
};

const fetchURIWithParams = async <T>(uri: string | Uri, params: RequestInit, dataType?: string): Promise<T> => {
	try {
		const response = await fetch(uri.toString(), params);
		return responseHandler<T>(response, dataType) as Promise<T>;
	} catch (error) {
		processCaughtError(uri as string, params, error as IError);
		return Promise.reject(error);
	}
};

const callFetchAndHandleJwt = async <T>(
	uri: string | Uri,
	params: RequestInit,
	additionalOptions?: IAdditionalOptions,
): Promise<T> => {
	const isIE11 = !(window as any).ActiveXObject && "ActiveXObject" in window;
	if (!additionalOptions?.externalUrl) {
		if (!isIE11 && getAuthToken() && !additionalOptions?.useCookieOnly) {
			(params.headers as Record<string, string>).Authorization = `Bearer ${getAuthToken()}`;
		} else {
			// In IE servers give cached responses thus adding cache burst param
			isIE11 && (uri as any).addQueryParam("cache-burst-param", Date.now());
			// tslint:disable-next-line:no-console
			console.info(
				`Didn't add Auth headers ${
					isIE11
						? "in IE11 browser"
						: additionalOptions?.useCookieOnly
							? "explicitly mentioned to use cookie"
							: "as token was missing"
				}, relying on auth cookie being passed`,
			);
		}
	}
	return fetchURIWithParams<T>(uri, params, additionalOptions?.dataType);
};

const mergeRequestOptions = (params: RequestInit, extraParams: RequestInit) => {
	const { headers, ...nonHeaderParams } = extraParams;
	params.headers = params.headers ? params.headers : {};
	Object.assign(params.headers, headers);
	Object.assign(params, { ...nonHeaderParams });
};

// exported helper functions

export const getOrgID = () => {
	return window.sessionjs?.getuserInfo()?.account_id;
};

export const getUserInfoFromJWT = () => {
	const jwtData = window.sessionjs?.getuserInfo();
	return {
		accountId: jwtData?.account_id,
		accountNumber: jwtData?.account_number,
		userId: jwtData?.user_id,
		isInternal: jwtData?.internal,
	};
};

export const getAuthToken = () => {
	return window.sessionjs?.token;
};

export const addQueryParamsToUri = <T extends object>(uri: Uri, params: T, includeEmptyString = false) => {
	params &&
		Object.keys(params).forEach((k) => {
			const val = (params as Record<string, unknown>)[k];
			// add false and 0 to query params
			if (val || val === false || val === 0 || (includeEmptyString && val === "")) {
				uri.addQueryParam(k, val as string | number | boolean);
			}
		});
};

export const getUri = <T>(
	uri: string | Uri,
	extraParams?: RequestInit,
	additionalOptions = defaultAdditionalOptions,
): Promise<T> => {
	const params: RequestInit = {
		method: "GET",
	};
	mergeRequestOptions(params, baseParams);
	extraParams && mergeRequestOptions(params, extraParams);
	return callFetchAndHandleJwt(uri, params, additionalOptions);
};

export const getUriWithBody = <T>(
	uri: string | Uri,
	body?: unknown,
	extraParams?: RequestInit,
	additionalOptions = defaultAdditionalOptions,
): Promise<T> => {
	const params: RequestInit = {
		method: "GET",
		headers: {
			"Content-Type": "application/json",
		},
		body: JSON.stringify(body),
	};
	mergeRequestOptions(params, baseParams);
	extraParams && mergeRequestOptions(params, extraParams);
	return callFetchAndHandleJwt(uri, params, additionalOptions);
};

export const postUri = <T>(
	uri: string | Uri,
	body?: unknown,
	extraParams?: RequestInit,
	additionalOptions = defaultAdditionalOptions,
): Promise<T> => {
	const params: RequestInit = {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			Accept: "application/json",
		},
		body: JSON.stringify(body),
	};
	mergeRequestOptions(params, baseParams);
	extraParams && mergeRequestOptions(params, extraParams);
	return callFetchAndHandleJwt(uri, params, additionalOptions);
};

export const postFormUri = <T>(
	uri: string,
	formData: FormData,
	extraParams?: RequestInit,
	additionalOptions = defaultAdditionalOptions,
): Promise<T> => {
	const params: RequestInit = {
		method: "POST",
		headers: {
			// Default enctype of html form element
			// All characters are encoded before sent (spaces are converted to "+" symbols, and special characters are converted to ASCII HEX values)
			// The value enctype="multipart/form-data" should be used in combination with the INPUT element, type="file".
			"Content-Type": "application/x-www-form-urlencoded",
		},
		body: formData,
	};
	mergeRequestOptions(params, baseParams);
	extraParams && mergeRequestOptions(params, extraParams);
	return callFetchAndHandleJwt(uri, params, additionalOptions);
};

export const putUri = <T>(
	uri: string | Uri,
	body: unknown,
	extraParams?: RequestInit,
	additionalOptions = defaultAdditionalOptions,
): Promise<T> => {
	const params: RequestInit = {
		method: "PUT",
		headers: {
			"Content-Type": "application/json",
		},
		body: JSON.stringify(body),
	};
	mergeRequestOptions(params, baseParams);
	extraParams && mergeRequestOptions(params, extraParams);
	return callFetchAndHandleJwt(uri, params, additionalOptions);
};

export const patchUri = <T>(
	uri: string | Uri,
	body: unknown,
	extraParams?: RequestInit,
	additionalOptions = defaultAdditionalOptions,
): Promise<T> => {
	const params: RequestInit = {
		method: "PATCH",
		headers: {
			"Content-Type": "application/json",
		},
		body: JSON.stringify(body),
	};
	mergeRequestOptions(params, baseParams);
	extraParams && mergeRequestOptions(params, extraParams);
	return callFetchAndHandleJwt(uri, params, additionalOptions);
};

export const deleteUri = <T>(
	uri: string | Uri,
	extraParams?: RequestInit,
	additionalOptions = defaultAdditionalOptions,
): Promise<T> => {
	const params: RequestInit = {
		method: "DELETE",
	};
	mergeRequestOptions(params, baseParams);
	extraParams && mergeRequestOptions(params, extraParams);
	return callFetchAndHandleJwt(uri, params, additionalOptions);
};

export const deleteUriWithBody = <T>(
	uri: string | Uri,
	body: unknown,
	extraParams?: RequestInit,
	additionalOptions = defaultAdditionalOptions,
): Promise<T> => {
	const params: RequestInit = {
		method: "DELETE",
		headers: {
			"Content-Type": "application/json",
		},
		body: JSON.stringify(body),
	};
	mergeRequestOptions(params, baseParams);
	extraParams && mergeRequestOptions(params, extraParams);
	return callFetchAndHandleJwt(uri, params, additionalOptions);
};
